import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Chip from "@material-ui/core/Chip";
import { LuPencil, LuTrash2, LuCheckCircle2 } from "react-icons/lu";
import { isArray } from "lodash";

import MainContainer from "../../components/MainContainer";
import {
  CrudListPage,
  CrudSearchField,
  CrudActionButton,
  useCrudListStyles,
} from "../../components/CrudListPage";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import QuickMessageDialog from "../../components/QuickMessageDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_QUICKMESSAGES") {
    const quickmessages = action.payload;
    const newQuickmessages = [];

    if (isArray(quickmessages)) {
      quickmessages.forEach((quickemessage) => {
        const quickemessageIndex = state.findIndex(
          (u) => u.id === quickemessage.id
        );
        if (quickemessageIndex !== -1) {
          state[quickemessageIndex] = quickemessage;
        } else {
          newQuickmessages.push(quickemessage);
        }
      });
    }

    return [...state, ...newQuickmessages];
  }

  if (action.type === "UPDATE_QUICKMESSAGES") {
    const quickemessage = action.payload;
    const quickemessageIndex = state.findIndex((u) => u.id === quickemessage.id);

    if (quickemessageIndex !== -1) {
      state[quickemessageIndex] = quickemessage;
      return [...state];
    }
    return [quickemessage, ...state];
  }

  if (action.type === "DELETE_QUICKMESSAGE") {
    const quickemessageId = action.payload;
    const quickemessageIndex = state.findIndex((u) => u.id === quickemessageId);
    if (quickemessageIndex !== -1) {
      state.splice(quickemessageIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useLocalStyles = makeStyles((theme) => ({
  mediaCell: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.75),
  },
  mediaChip: {
    fontSize: "0.75rem",
    height: 24,
    fontWeight: 600,
    borderRadius: 999,
  },
  mediaIcon: {
    fontSize: "1rem",
    lineHeight: 1,
  },
  globalIcon: {
    color: theme.palette.success.main,
  },
}));

const getMediaIcon = (type) => {
  switch (type) {
    case "audio":
      return "🎵";
    case "image":
      return "🖼️";
    case "video":
      return "🎥";
    default:
      return "📎";
  }
};

const getMediaColor = (type) => {
  switch (type) {
    case "audio":
      return "secondary";
    case "image":
      return "primary";
    default:
      return "default";
  }
};

const Quickemessages = () => {
  const classes = useCrudListStyles({ tableMinWidth: 720 });
  const localClasses = useLocalStyles();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedQuickemessage, setSelectedQuickemessage] = useState(null);
  const [deletingQuickemessage, setDeletingQuickemessage] = useState(null);
  const [quickemessageModalOpen, setQuickMessageDialogOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [quickemessages, dispatch] = useReducer(reducer, []);
  const { user, socket } = useContext(AuthContext);

  const fetchQuickemessages = async () => {
    try {
      const { data } = await api.get("/quick-messages", {
        params: { searchParam, pageNumber },
      });

      dispatch({ type: "LOAD_QUICKMESSAGES", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchQuickemessages();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (!socket || !user?.companyId) return;

    const eventName = `company-${user.companyId}-quickmessage`;

    const onQuickMessageEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUICKMESSAGES", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUICKMESSAGE", payload: +data.id });
      }
    };

    socket.on(eventName, onQuickMessageEvent);

    return () => {
      socket.off(eventName, onQuickMessageEvent);
    };
  }, [socket, user?.companyId]);

  const handleOpenQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(true);
  };

  const handleCloseQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(false);
    fetchQuickemessages();
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditQuickemessage = (quickemessage) => {
    setSelectedQuickemessage(quickemessage);
    setQuickMessageDialogOpen(true);
  };

  const handleDeleteQuickemessage = async (quickemessageId) => {
    try {
      await api.delete(`/quick-messages/${quickemessageId}`);
      toast.success(i18n.t("quickemessages.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingQuickemessage(null);
    setSearchParam("");
    setPageNumber(1);
    dispatch({ type: "RESET" });
    fetchQuickemessages();
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPageNumber((prevState) => prevState + 1);
    }
  };

  const getMediaTypeDisplay = (quickmessage) => {
    if (!quickmessage.mediaName) {
      return i18n.t("quickMessages.noAttachment");
    }

    const mediaType = quickmessage.mediaType || "document";

    return (
      <span className={localClasses.mediaCell}>
        <span className={localClasses.mediaIcon}>{getMediaIcon(mediaType)}</span>
        <Chip
          size="small"
          label={mediaType}
          color={getMediaColor(mediaType)}
          className={localClasses.mediaChip}
        />
      </span>
    );
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingQuickemessage &&
          `${i18n.t("quickMessages.confirmationModal.deleteTitle")} ${deletingQuickemessage.shortcode}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteQuickemessage(deletingQuickemessage.id)}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QuickMessageDialog
        resetPagination={() => {
          setPageNumber(1);
          fetchQuickemessages();
        }}
        open={quickemessageModalOpen}
        onClose={handleCloseQuickMessageDialog}
        aria-labelledby="form-dialog-title"
        quickemessageId={selectedQuickemessage && selectedQuickemessage.id}
      />
      <CrudListPage
        title={i18n.t("quickMessages.title")}
        tableMinWidth={720}
        onScroll={handleScroll}
        toolbar={
          <>
            <div className={classes.toolbarItem}>
              <CrudSearchField
                classes={classes}
                value={searchParam}
                onChange={handleSearch}
                placeholder={i18n.t("quickMessages.searchPlaceholder")}
              />
            </div>
            <div className={classes.toolbarItem}>
              <Button
                variant="contained"
                color="primary"
                className={classes.primaryButton}
                onClick={handleOpenQuickMessageDialog}
              >
                {i18n.t("quickMessages.buttons.add")}
              </Button>
            </div>
          </>
        }
      >
        <Table size="small" stickyHeader className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell align="center">
                {i18n.t("quickMessages.table.shortcode")}
              </TableCell>
              <TableCell align="center">Mídia</TableCell>
              <TableCell align="center">
                {i18n.t("quickMessages.table.status")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("quickMessages.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {quickemessages.map((quickemessage) => (
              <TableRow key={quickemessage.id}>
                <TableCell align="center">{quickemessage.shortcode}</TableCell>
                <TableCell align="center">
                  {getMediaTypeDisplay(quickemessage)}
                </TableCell>
                <TableCell align="center">
                  {quickemessage.geral === true && (
                    <LuCheckCircle2
                      size={18}
                      strokeWidth={1.75}
                      className={localClasses.globalIcon}
                      aria-label="Global"
                    />
                  )}
                </TableCell>
                <TableCell align="center" className={classes.actionsCell}>
                  <CrudActionButton
                    classes={classes}
                    icon={LuPencil}
                    onClick={() => handleEditQuickemessage(quickemessage)}
                    ariaLabel="Editar resposta rápida"
                  />
                  <CrudActionButton
                    classes={classes}
                    icon={LuTrash2}
                    onClick={() => {
                      setConfirmModalOpen(true);
                      setDeletingQuickemessage(quickemessage);
                    }}
                    ariaLabel="Excluir resposta rápida"
                    danger
                  />
                </TableCell>
              </TableRow>
            ))}
            {loading &&
              Array.from({ length: 5 }).map((_, idx) => (
                <TableRowSkeleton key={`skeleton-${idx}`} columns={4} />
              ))}
          </TableBody>
        </Table>
      </CrudListPage>
    </MainContainer>
  );
};

export default Quickemessages;
